import React, { useEffect } from 'react';
import Select from 'react-select';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import moment from 'moment';

import Spinner from '../../../../components/spinner/Spinner';
import GenderExperience
    from '../../../../components/filters/players-filter/components/gender-experience/GenderExperience';
import ScheduleCalendar
    from '../../../../components/filters/players-filter/components/schedule-calendar/ScheduleCalendar';
import PreferencesConditions
    from '../../../../components/filters/players-filter/components/preferences-conditions/PreferencesConditions';
import Slider from '../../../../components/slider/Slider';

import Header from '../../../test/components/header/Header';
import Footer from '../../../test/components/footer/Footer';

import User from '../../../../stores/models/User';
import Court from '../../../../stores/models/Court';
import UserStore from '../../../../stores/UserStore';
import CourtStore from '../../../../stores/CourtStore';
import { useStore, useLocalStore } from '../../../../stores';
import { backIcon, cancelIcon, finishIcon, logoutIcon, nextIcon, removeUserIcon } from '../../../../utils/icons';

import rawLocations from '../../assets/resources/locations.json';
import languages from '../../assets/resources/languages.json';

import styles from './EditPage.module.scss';

const locations = Object.entries(rawLocations).reduce<{ country: string, city: string, name: string }[]>((acc,
    [country, cities]) => ([
    ...acc,
    ...cities.map(city => ({
        country,
        city,
        name: `${country}, ${city}`,
    })),
]), []);

function toBase64(file: any): Promise<string> {
    return new Promise(async (resolve, reject) => {
        if (typeof file === 'string') {
            file = await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.addEventListener('load', () => resolve(xhr.response));
                xhr.addEventListener('error', reject);
                xhr.open('GET', file);
                xhr.responseType = 'blob';
                xhr.send();
            });
        }

        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result as string));
        reader.addEventListener('error', reject);
        reader.readAsDataURL(file);
    });
}

type LocalStore = {
    genderExperience: {
        active: string[]
    }
    locations: any[],

    selectedCourt: Court | null,

    calendar: {
        schedule: any[],
    },
    calendarTouched: boolean,

    preference: {
        court: string[],
        play: string[],
        training: string[],
    },
    preferenceTouched: boolean,

    user: User | null,
    courts: Court[],

    isFirstNameValid: boolean,
    isLocationValid: boolean,
    isLanguagesValid: boolean,
};

export default observer(function EditPage() {
    const history = useHistory();
    const location = useLocation<{ role?: string }>();

    const { authStore } = useStore();
    const { localStore } = useLocalStore<LocalStore>({
        genderExperience: {
            active: ['age', 'ntrp'],
        },
        locations: [],

        selectedCourt: null,

        calendar: {
            schedule: [],
        },
        calendarTouched: false,

        preference: {
            court: [],
            play: [],
            training: [],
        },
        preferenceTouched: false,

        user: authStore.user?.clone() || null,
        courts: [],

        isFirstNameValid: true,
        isLocationValid: true,
        isLanguagesValid: true,
    });

    useEffect(() => {
        CourtStore.find().then((courts) => {
            localStore.courts = courts;
        });
    }, []);

    useEffect(() => {
        localStore.user = authStore.user?.clone() || null;

        localStore.calendarTouched = false;
        localStore.preferenceTouched = false;
    }, [authStore.user]);

    if (!localStore.user) {
        return (
            <div>Not found</div>
        );
    }

    if (!localStore.user.gender) {
        localStore.user.gender = 'male';
    }
    if (!localStore.user.role) {
        localStore.user.role = location.state.role || 'players';
    }

    return (
        <div className={styles.container}>
            <div className={styles.top}>
                <Header
                    title={localStore.user.role === 'players'
                        ? 'Editing Player Profile'
                        : localStore.user.role === 'coaches'
                            ? 'Editing Coach Profile'
                            : 'Editing Courts Profile'}
                    left={{
                        icon: backIcon,
                        onClick: () => history.goBack(),
                    }}
                    right={{
                        icon: logoutIcon,
                        onClick: () => authStore.logout(),
                    }}
                />
            </div>

            <div className={styles.content}>
                <div className={styles.contentContainer}>
                    {!authStore.user?.role && (
                        <div>
                            <ul className="progressbar">
                                <li className="active">Registered</li>
                                <li className="active">Select role</li>
                                <li>Additional info</li>
                            </ul>
                        </div>
                    )}

                    <div style={{ position: 'relative', zIndex: 100 }}>
                        <div className={styles.profileRole}>
                            <label
                                htmlFor="upload-button"
                                className={classNames(styles.profileRoleItem, styles.profileRoleItemActive)}
                            >
                                {localStore.user.role === 'players' && (
                                    <img
                                        className={styles.profileRoleItemBackground}
                                        src={localStore.user.image || require('../../assets/images/player.png').default}
                                        alt=""
                                    />
                                )}
                                {localStore.user.role === 'coaches' && (
                                    <img
                                        className={styles.profileRoleItemBackground}
                                        src={localStore.user.image || require('../../assets/images/coach.png').default}
                                        alt=""
                                    />
                                )}
                                {localStore.user.role === 'courts' && (
                                    <img
                                        className={styles.profileRoleItemBackground}
                                        src={localStore.user.image || require('../../assets/images/admin.png').default}
                                        alt=""
                                    />
                                )}

                                <img
                                    className={styles.profileRoleItemIcon}
                                    src={require('../../assets/icons/camera.png').default}
                                    alt=""
                                />

                                <input
                                    id="upload-button"
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={async (event) => {
                                        if (event.target.files && localStore.user) {
                                            localStore.user.image = await toBase64(event.target.files[0]);
                                        }
                                    }}
                                />
                            </label>

                            <div
                                className={classNames(styles.profileRoleItem, styles.profileRoleItemActive)}
                                style={{
                                    flex: 1,
                                    justifyContent: 'flex-start',
                                    boxShadow: 'none',
                                    overflow: 'initial',
                                }}
                            >
                                <div className={styles.form}>
                                    <input
                                        type="text"
                                        name="firstName"
                                        placeholder="First Name"
                                        className={classNames(styles.formInput, {
                                            [styles.formInputInvalid]: !localStore.isFirstNameValid,
                                        })}
                                        value={localStore.user.firstName}
                                        onChange={(event) => {
                                            if (localStore.user) {
                                                localStore.user.firstName = event.target.value;
                                            }
                                        }}
                                    />

                                    <input
                                        type="text"
                                        name="lastName"
                                        placeholder="Last Name"
                                        className={styles.formInput}
                                        value={localStore.user.lastName}
                                        onChange={(event) => {
                                            if (localStore.user) {
                                                localStore.user.lastName = event.target.value;
                                            }
                                        }}
                                    />

                                    <Select
                                        isClearable={false}
                                        cacheOptions
                                        className={classNames(styles.formInput, {
                                            [styles.formInputInvalid]: !localStore.isLocationValid,
                                        })}
                                        minMenuHeight={100}
                                        maxMenuHeight={140}
                                        placeholder="Location"
                                        styles={{
                                            container: (provided, state) => ({
                                                ...provided,
                                                padding: '6px 0 !important',
                                            }),
                                            control: () => ({
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                height: 24,
                                            }),
                                            dropdownIndicator: (provided, state) => ({
                                                ...provided,
                                                padding: 3,
                                            }),
                                            valueContainer: (provided, state) => ({
                                                ...provided,
                                                padding: 0,
                                                height: '100%',
                                            }),
                                        }}
                                        options={localStore.locations}
                                        value={localStore.user.location && {
                                            label: localStore.user.location,
                                            value: localStore.user.location,
                                        }}
                                        onInputChange={(value) => {
                                            if (value.length >= 3) {
                                                localStore.locations = locations
                                                    .filter(location => location.city.toLowerCase().includes(value.toLowerCase()))
                                                    .map(location => ({
                                                        label: location.name,
                                                        value: location.name,
                                                    }));
                                            } else {
                                                localStore.locations = [];
                                            }
                                        }}
                                        onChange={(item) => {
                                            if (localStore.user) {
                                                localStore.user.location = item.value;
                                            }
                                        }}
                                    />

                                    <Select
                                        isMulti
                                        isClearable={false}
                                        cacheOptions
                                        className={classNames(styles.formInput, {
                                            [styles.formInputInvalid]: !localStore.isLanguagesValid,
                                        })}
                                        minMenuHeight={100}
                                        maxMenuHeight={140}
                                        placeholder="Languages"
                                        styles={{
                                            container: (provided, state) => ({
                                                ...provided,
                                                padding: '6px 0 !important',
                                            }),
                                            control: () => ({
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                height: 24,
                                            }),
                                            dropdownIndicator: (provided, state) => ({
                                                ...provided,
                                                padding: 3,
                                            }),
                                            valueContainer: (provided, state) => ({
                                                ...provided,
                                                padding: 0,
                                                height: '100%',
                                            }),
                                        }}
                                        options={[
                                            {
                                                label: 'Popular',
                                                // eslint-disable-next-line
                                                // @ts-ignore
                                                options: languages.filter(language => language.popular).map(language => ({
                                                    label: language.code,
                                                    value: language.code,
                                                })),
                                            },
                                            {
                                                label: 'All',
                                                options: languages.map(language => ({
                                                    label: language.code,
                                                    value: language.code,
                                                })),
                                            },
                                        ]}
                                        value={localStore.user.languages.map(language => ({
                                            label: language,
                                            value: language,
                                        }))}
                                        onChange={(items) => {
                                            if (localStore.user) {
                                                localStore.user.languages = items?.map(item => item.value) || [];
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {localStore.user.role !== 'courts' && (
                        <div>
                            <GenderExperience
                                mode="single"
                                gender={[localStore.user.gender]}
                                rating={{
                                    active: localStore.genderExperience.active,

                                    age: [localStore.user.age || 25],
                                    experience: [localStore.user.experience || 0],
                                    ntrp: [localStore.user.ratings.ntrp || 3.5],
                                    ello: [localStore.user.ratings.ello || 1200],
                                }}
                                onChange={(gender, ratings) => {
                                    if (!localStore.user) {
                                        return;
                                    }

                                    localStore.genderExperience.active = ratings.active;

                                    localStore.user.gender = gender[0];

                                    localStore.user.age = ratings.age[0];
                                    localStore.user.experience = ratings.experience[0];
                                    localStore.user.ratings.ntrp = ratings.ntrp[0];
                                    localStore.user.ratings.ello = ratings.ello[0];
                                }}
                            />

                            <div style={{ padding: 10 }}>
                                <div
                                    style={{
                                        marginBottom: 16,
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <span>Read more about </span>
                                    <a
                                        href="https://assets.usta.com/assets/639/15/National%20tennis%20Rating%20Program.pdf"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <strong>NTRP</strong>
                                    </a>
                                    <span> and other rating systems.</span>
                                </div>

                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 1.0 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>1.0</strong> - This player is just starting to play tennis.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 1.5 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>1.5</strong> - This player has limited experience and is still working primarily on
                                    getting the ball into play.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 2.0 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>2.0</strong> - This player needs on-court experience. This player has obvious stroke
                                    weaknesses but is familiar with basic positions for singles and doubles play.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 2.5 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>2.5</strong> - This player is learning to judge where the ball is going although court
                                    coverage is weak. Can sustain a short rally of slow pace with other players of the same ability.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 3.0 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>3.0</strong> - This player is fairly consistent when hitting medium paced shots, but is
                                    not comfortable with all strokes and lacks execution when trying for directional control, depth,
                                    or power. Most common doubles formation is one-up, one-back.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 3.5 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>3.5</strong> - This player has achieved improved stroke dependability with directional
                                    control on moderate shots, but still lacks depth and variety. This player exhibits more
                                    aggressive net play, has improved court coverage, and is developing teamwork in doubles.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 4.0 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>4.0</strong> - This player has dependable strokes, including directional control and
                                    depth on both forehand and backhand sides on moderate shots, plus the ability to use lobs,
                                    overheads, approach shots and volleys with some success. This player occasionally forces errors
                                    when serving. Rallies may be lost due to impatience. Teamwork in doubles is evident.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 4.5 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>4.5</strong> - This player has begun to master the use of power and spins and is
                                    beginning to handle pace, has sound footwork, can control depth of shots, and is beginning to
                                    vary game plan according to opponents. This player can hit first serves with power and accuracy
                                    and place the second serve. This player tends to over hit on difficult shots. Aggressive net
                                    play is common in doubles.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 5.0 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>5.0</strong> - This player has good shot anticipation and frequently has an outstanding
                                    shot or attribute around which a game may be structured. This player can regularly hit winners
                                    or force errors off of short balls and can put away volleys, can successfully execute lobs,
                                    drop shots, half volleys, overhead smashes, and has good depth and spin on most 2nd serves.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 5.5 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>5.5</strong> - This player has developed power and/or consistency as a major weapon.
                                    This player can vary strategies and styles of play in a competitive situation and hits
                                    dependable shots in a stress situation.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 6.0 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>6.0</strong> - This player typically has had intensive training for national tournament
                                    competition at the junior and collegiate levels and has obtained a sectional and/or national
                                    ranking.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 6.5 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>6.5</strong> - This player has a reasonable chance of succeeding at the 7.0 level and
                                    has extensive satellite tournament experience.
                                </div>
                                <div
                                    style={{
                                        display: localStore.user.ratings.ntrp === 7.0 ? 'block' : 'none',
                                        fontWeight: 100,
                                        textAlign: 'center',
                                    }}
                                >
                                    <strong>7.0</strong> - This player is a world-class player who is committed to tournament
                                    competition on the international level and whose major source of income is tournament prize
                                    winnings.
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 style={{ margin: '32px 0', textAlign: 'center' }}>
                            {localStore.user.role === 'courts' ? 'Choose the court you administrate' : 'Choose your home court'}
                        </h3>

                        <div
                            className="slider-container"
                            style={{
                                padding: 0,
                                marginBottom: 16,
                                minHeight: 130,
                            }}
                        >
                            {localStore.courts.length === 0 && (
                                <Spinner
                                    style={{
                                        position: 'absolute',
                                        zIndex: 1,
                                    }}
                                />
                            )}

                            <Slider
                                className="slider-courts"
                                type="courts"
                                initialIndex={0}
                                onChange={(activeIndex) => {
                                    if (localStore.user) {
                                        const courts = [...localStore.user.courts, ...localStore.courts]
                                            .filter((item: any, i: number,
                                                items: any[]) => items.findIndex(d => d.id === item.id) === i);

                                        localStore.selectedCourt = courts[activeIndex];
                                    }
                                }}
                            >
                                {[...localStore.user.courts, ...localStore.courts].filter((item: any, i: number,
                                    items: any[]) => items.findIndex(d => d.id === item.id) === i).map(court => (
                                    <div
                                        key={court.id}
                                        className="court-card"
                                        style={{ backgroundImage: `url(${court.image})` }}
                                    >
                                        <div className="header-block">
                                            <div className="header-container">{court.name}</div>
                                        </div>

                                        <div className="text-block">
                                            <div className="text-container">
                                                <div className="upper-field">
                                                    {court.address}
                                                </div>
                                                <div className="lower-field">
                                                    {court.city}, {court.country}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </Slider>
                        </div>

                        <div style={{ textAlign: 'center', marginBottom: 20 }}>
                            {localStore.user.role === 'courts'
                                ? (
                                    <>
                                        Can’t find here your court? <strong><Link to="/courts/create">Create</Link></strong> new one for <strong>FREE</strong>!
                                    </>
                                )
                                : (
                                    <>
                                        Select 1 or more place where you want or prefer to play!
                                        You can change then your choice anytime!
                                    </>
                                )}
                        </div>
                    </div>

                    <div>
                        <ScheduleCalendar
                            style={{ height: 'auto ' }}
                            title={{
                                schedule: 'My schedule',
                                specific: '',
                            }}
                            calendar={{
                                type: 'schedule',

                                schedule: localStore.calendarTouched
                                    ? localStore.calendar.schedule
                                    : (localStore.user?.role === 'courts' ? localStore.user.courts[0]?.calendar : localStore.user?.calendar)?.map((calendar: any) => ({
                                        date: moment(calendar.date).format('MMM DD'),
                                        time: calendar.time,
                                    })) || [],
                                specific: [],
                            }}
                            onChange={(calendar) => {
                                localStore.calendar = calendar;
                                localStore.calendarTouched = true;
                            }}
                        />

                        <div style={{ textAlign: 'center', marginTop: 10, marginBottom: 20 }}>
                            &quot;<strong>My schedule</strong>&quot; allows you indicate more accurate
                            your preferred play/training times on certain dates.
                        </div>
                    </div>

                    {['players', 'coaches'].includes(localStore.user.role) && (
                        <div>
                            <div style={{ margin: '20px 0' }}>
                                <PreferencesConditions
                                    filter={{
                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                        // @ts-ignore
                                        type: localStore.user.role,
                                        preference: localStore.preferenceTouched ? localStore.preference : localStore.user.preference,
                                    }}
                                    onChange={(filter) => {
                                        localStore.preference = filter.preference;
                                        localStore.preferenceTouched = true;
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.bottom}>
                <Footer
                    buttons={[
                        {
                            title: 'Back',
                            icon: cancelIcon,
                            onClick: () => history.goBack(),
                        },
                        {
                            title: 'Remove Account',
                            icon: removeUserIcon,
                            onClick: async () => {
                                if (!localStore.user) {
                                    return;
                                }

                                if (window.confirm('Are you sure that you want to delete all yours tennis earnings and information forever?')) {
                                    await UserStore.deleteById(localStore.user.id);
                                    await authStore.logout();

                                    history.replace('/auth/login');
                                }
                            },
                        },
                        {
                            title: 'Finish',
                            icon: finishIcon,
                            onClick: () => runInAction(async () => {
                                if (!localStore.user) {
                                    return;
                                }

                                localStore.isFirstNameValid = !!localStore.user.firstName;
                                localStore.isLocationValid = !!localStore.user.location;
                                localStore.isLanguagesValid = !!localStore.user.languages.length;
                                if (!localStore.isFirstNameValid || !localStore.isLocationValid || !localStore.isLanguagesValid) {
                                    return;
                                }
                                if (!localStore.selectedCourt) {
                                    return;
                                }

                                try {
                                    const user = localStore.user.clone();
                                    if (localStore.user.role === 'players') {
                                        user.image = user.image || await toBase64(require('../../assets/images/player.png').default);
                                    } else if (localStore.user.role === 'coaches') {
                                        user.image = user.image || await toBase64(require('../../assets/images/coach.png').default);
                                    } else if (localStore.user.role === 'courts') {
                                        user.image = user.image || await toBase64(require('../../assets/images/admin.png').default);
                                    }
                                    user.courts = [localStore.selectedCourt, ...user.courts]
                                        .filter((item: any, i: number,
                                            items: any[]) => items.findIndex(d => d.id === item.id) === i);

                                    if (localStore.calendarTouched) {
                                        user.calendar = localStore.calendar.schedule;
                                    }

                                    if (localStore.preferenceTouched) {
                                        user.preference.court = localStore.preference.court;
                                        user.preference.play = localStore.preference.play;
                                        user.preference.training = localStore.preference.training;
                                    }

                                    await UserStore.saveById(user.id, user);

                                    if (authStore.user) {
                                        authStore.user = user;

                                        if (authStore.user.role === 'courts') {
                                            history.replace(`/courts/${authStore.user.courts[0].id}`);
                                        } else {
                                            history.replace(`/players/${authStore.user.id}`);
                                        }
                                    }
                                } catch (err) {
                                    console.log(err);
                                }
                            }),
                        },
                    ]}
                />
            </div>
        </div>
    );
});
